import React, { useCallback, useContext, useMemo } from "react";
import BasicAccountApplicationAPI from "../../api/commercial/basic-account-application";
import { ApplicationFlowSteps } from "../../route/application/authenticated/application-flow/types";
import { ErrorContext } from "../../route/error/provider";
import CompanyMapper from "../home/company-mapper";
import { CompanyModel, LegalForm } from "../home/types";
import RegulatoryInformation from "./index";
import RegulatoryInformationMapper from "./mapper";
import { RegulatoryInformationModel } from "./types";
import { BasicAccountApplicationDocument } from "../../api/commercial/generated-types";

interface RegulatoryInformationAdapterProps {
  basicAccountApplication: BasicAccountApplicationDocument;
  triggerReload: () => void;
  setStep: (step: ApplicationFlowSteps) => void;
}

export const RegulatoryInformationAdapter: React.FC<
  RegulatoryInformationAdapterProps
> = ({
  basicAccountApplication,
  triggerReload,
  setStep,
}: RegulatoryInformationAdapterProps): React.JSX.Element => {
  const { setError } = useContext(ErrorContext);

  const basicApplicationRef: string = useMemo(
    () => basicAccountApplication.applicationRef,
    [basicAccountApplication.applicationRef],
  );

  const companyModel: CompanyModel = useMemo(
    () => CompanyMapper.mapDocumentToPageModel(basicAccountApplication.company),
    [basicAccountApplication.company],
  );

  const regulatoryInformationModel: RegulatoryInformationModel = useMemo(
    () =>
      RegulatoryInformationMapper.mapDocumentToPageModel(
        basicAccountApplication,
        companyModel.legalForm,
      ),
    [basicAccountApplication, companyModel.legalForm],
  );

  // The only case where the legal form matches 1:1 with the sole proprietorship. Hence, it makes sense to be specific.
  const isSoleProprietorship: boolean = useMemo(
    () =>
      companyModel
        ? companyModel.legalForm === LegalForm.SOLE_PROPRIETORSHIP
        : false,
    [companyModel],
  );

  const context = useMemo(
    () => ({
      isSoleProprietorship,
      companyName: companyModel.name,
    }),
    [companyModel.name, isSoleProprietorship],
  );

  const previous = useCallback(() => {
    setStep(ApplicationFlowSteps.BASIC_ACCOUNT_BENEFITS);
  }, [setStep]);

  const next = useCallback(
    async (model: RegulatoryInformationModel) => {
      try {
        const request = RegulatoryInformationMapper.mapPageModelToRequest(
          model,
          basicApplicationRef,
        );
        await BasicAccountApplicationAPI.updateRegulatoryInfo(request);
      } catch (e) {
        setError(e);
      }

      triggerReload();
    },
    [basicApplicationRef, setError, triggerReload],
  );

  return (
    <RegulatoryInformation
      initial={regulatoryInformationModel}
      context={context}
      previous={previous}
      next={next}
    />
  );
};
